// Lightweight on-demand loader for the Google Maps JavaScript API.
// We don't bundle a Maps SDK — most of the app doesn't use Maps JS (only
// Places autocomplete + static-map URLs). Mobile route map mode loads it
// lazily when the tech taps "Map". Subsequent toggles reuse the namespace.

let loadingPromise = null;

const DEFAULT_LIBRARIES = ['marker', 'geometry'];

/**
 * Load (or return) the Google Maps JS namespace.
 * Resolves with `window.google.maps` once the script has finished loading.
 *
 * @param {Object} [options]
 * @param {string[]} [options.libraries] — extra libraries to load (default: marker, geometry)
 * @returns {Promise<typeof google.maps>}
 */
export const loadGoogleMaps = (options = {}) => {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
  if (loadingPromise) return loadingPromise;

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!apiKey) return Promise.reject(new Error('Google API key missing'));

  const libraries = (options.libraries || DEFAULT_LIBRARIES).join(',');
  const callbackName = `__gmaps_cb_${Date.now()}`;

  loadingPromise = new Promise((resolve, reject) => {
    window[callbackName] = () => {
      delete window[callbackName];
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps loaded but namespace missing'));
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries}&callback=${callbackName}`;
    script.onerror = () => {
      delete window[callbackName];
      loadingPromise = null;
      reject(new Error('Failed to load Google Maps JS'));
    };
    document.head.appendChild(script);
  });

  return loadingPromise;
};

export default loadGoogleMaps;
